/**
 * `Intl` formatters, cached. Every caller names its locale: the reader's for
 * text a person reads (`useAppLocale()` in a client component, `getLocale()`
 * from `next-intl/server` on the server), `MACHINE_LOCALE` for text a program
 * or a model reads (API, MCP and agent output, logs).
 *
 * Client-safe and dependency-free. An `Intl` constructor costs tens of
 * microseconds; a list or table that formats per row reuses one instance per
 * locale and options instead.
 */

/** A BCP 47 tag, or an app locale (`pt-br`): `Intl` accepts either. */
export type FormatLocale = string

/** The locale of machine-read text: API, MCP and agent output, logs. */
export const MACHINE_LOCALE = 'en-US'

/** Distinct (locale, options) pairs are few; the cap only bounds a leak. */
const MAX_CACHED = 500

const formatters = new Map<string, unknown>()

function cached<T>(
  kind: string,
  locale: FormatLocale,
  options: object | undefined,
  create: () => T
): T {
  const key = `${kind}\u0000${locale}\u0000${options === undefined ? '' : JSON.stringify(options)}`
  const hit = formatters.get(key)
  if (hit !== undefined) {
    return hit as T
  }
  const created = create()
  if (formatters.size >= MAX_CACHED) {
    formatters.clear()
  }
  formatters.set(key, created)
  return created
}

export function numberFormat(
  locale: FormatLocale,
  options?: Intl.NumberFormatOptions
): Intl.NumberFormat {
  return cached(
    'number',
    locale,
    options,
    () => new Intl.NumberFormat(locale, options)
  )
}

/** Throws a `RangeError` for an unknown `timeZone`, like `Intl` itself. */
export function dateTimeFormat(
  locale: FormatLocale,
  options?: Intl.DateTimeFormatOptions
): Intl.DateTimeFormat {
  return cached(
    'dateTime',
    locale,
    options,
    () => new Intl.DateTimeFormat(locale, options)
  )
}

export function relativeTimeFormat(
  locale: FormatLocale,
  options?: Intl.RelativeTimeFormatOptions
): Intl.RelativeTimeFormat {
  return cached(
    'relativeTime',
    locale,
    options,
    () => new Intl.RelativeTimeFormat(locale, options)
  )
}

export function pluralRules(
  locale: FormatLocale,
  options?: Intl.PluralRulesOptions
): Intl.PluralRules {
  return cached(
    'plural',
    locale,
    options,
    () => new Intl.PluralRules(locale, options)
  )
}

export function listFormat(
  locale: FormatLocale,
  options?: Intl.ListFormatOptions
): Intl.ListFormat {
  return cached(
    'list',
    locale,
    options,
    () => new Intl.ListFormat(locale, options)
  )
}

/**
 * A count in the locale's short form, to one decimal: `250K`, `2.5M` in
 * English, `250.000` and `2,5 Mio.` in German.
 */
export function formatCompactNumber(
  locale: FormatLocale,
  value: number
): string {
  return numberFormat(locale, {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value)
}

/**
 * How a compact unit or relative time reads in `locale`: narrow in English
 * (`5m`, `30d`, `20MB`, `5m ago`), short in every other language (`5 min`,
 * `30 dagen`, `20 Mo`, `il y a 5 min`). Outside English a narrow form is
 * often a bare letter that reads as another unit (Swedish `5m` is five
 * metres, Turkish `5d` five minutes), runs into the number, or drops the
 * words of a relative time (French `-5 min`).
 */
export function compactUnitDisplay(locale: FormatLocale): 'narrow' | 'short' {
  const tag = locale.toLowerCase()
  return tag === 'en' || tag.startsWith('en-') ? 'narrow' : 'short'
}

/**
 * One value in one unit, compact (`compactUnitDisplay`): `250ms`, `1.5s`,
 * `12m`, `3h` in English, `1,5 Sek.` in German, `5 min` in Swedish.
 */
export function formatNarrowUnit(
  locale: FormatLocale,
  unit: 'millisecond' | 'second' | 'minute' | 'hour' | 'day',
  value: number,
  fractionDigits = 0
): string {
  return numberFormat(locale, {
    style: 'unit',
    unit,
    unitDisplay: compactUnitDisplay(locale),
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value)
}
